import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { ErrorComponent } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { api, type AdminNamedWarmPoolRow } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Boxes, ExternalLink, Loader2, Palette, Plus, RotateCw, Trash2 } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

type PoolStatus = "Claimed" | "Ready" | "Issuing cert" | "Provisioning" | "Error";

// Derive a single human status from the pool row + its joined container.
function statusOf(r: AdminNamedWarmPoolRow): PoolStatus {
  if (r.claimedByOrgId) return "Claimed";
  if (r.containerStatus === "error" || r.customDomainStatus === "error") return "Error";
  if (!r.containerId || r.containerStatus !== "running") return "Provisioning";
  if (r.customDomainStatus === "active") return "Ready";
  return "Issuing cert"; // container up, cert pending/verifying
}

// A row is still "settling" (worth polling) while it's provisioning or
// issuing its cert — i.e. not yet Ready, Claimed, or Errored.
function isSettling(r: AdminNamedWarmPoolRow): boolean {
  const s = statusOf(r);
  return s === "Provisioning" || s === "Issuing cert";
}

const STATUS_VARIANT: Record<PoolStatus, "default" | "secondary" | "outline" | "destructive"> = {
  Claimed: "outline",
  Ready: "default",
  "Issuing cert": "secondary",
  Provisioning: "secondary",
  Error: "destructive",
};

const SUBDOMAIN_RE = /^[a-z0-9-]{3,30}$/;

// Keep table cells' links from also triggering the row-click (which opens
// the detail sheet).
function stop(e: React.MouseEvent) {
  e.stopPropagation();
}

export function AdminNamedWarmPoolPage() {
  useDocumentTitle("Admin: Named warm pool");
  const query = useQuery({
    queryKey: ["admin-named-warm-pool"],
    queryFn: api.admin.namedWarmPool,
    // Poll while anything is mid-provision so the UI reflects progress live.
    refetchInterval: (q) => (q.state.data?.data.some(isSettling) ? 4000 : false),
  });

  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Look the selected entry up from live query data so the sheet reflects
  // status changes as provisioning progresses.
  const selected = query.data?.data.find((r) => r.id === selectedId) ?? null;

  const columns: ColumnDef<AdminNamedWarmPoolRow>[] = [
    {
      accessorKey: "companyName",
      header: "Company",
      cell: ({ row }) => <span className="font-medium">{row.original.companyName}</span>,
    },
    {
      accessorKey: "reservedSubdomain",
      header: "Subdomain",
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.original.reservedSubdomain}</span>
      ),
    },
    {
      id: "url",
      header: "Branded URL",
      cell: ({ row }) =>
        row.original.customDomainFqdn ? (
          <a
            href={`https://${row.original.customDomainFqdn}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stop}
            className="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
          >
            {row.original.customDomainFqdn}
            <ExternalLink className="size-3" />
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: "domains",
      header: "Domains",
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.domains.map((d) => (
            <Badge key={d} variant="secondary" className="font-mono text-[10px]">
              {d}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => {
        const s = statusOf(row.original);
        return <Badge variant={STATUS_VARIANT[s]}>{s}</Badge>;
      },
    },
    {
      id: "claimedBy",
      header: "Claimed by",
      cell: ({ row }) =>
        row.original.claimedByOrgId ? (
          <span className="text-sm">
            {row.original.claimedByOrgName ?? row.original.claimedByOrgId}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Named warm pool"
          subtitle="Pre-warmed brains reserved for specific customers by email domain."
        />
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            {query.isFetching ? "Loading…" : "Refresh"}
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            New pool entry
          </Button>
        </div>
      </div>

      {query.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : query.isError || !query.data ? (
        <ErrorComponent error={query.error ?? "Failed to load"} />
      ) : (
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            <strong className="text-foreground">{query.data.data.length}</strong> rows ·{" "}
            <strong className="text-foreground">
              {query.data.data.filter((r) => !r.claimedByOrgId).length}
            </strong>{" "}
            unclaimed ·{" "}
            <strong className="text-foreground">
              {query.data.data.filter(isSettling).length}
            </strong>{" "}
            still provisioning
          </div>
          {query.data.data.length > 0 ? (
            <DataTable
              columns={columns}
              data={query.data.data}
              onRowClick={(r) => setSelectedId(r.id)}
            />
          ) : (
            <p className="text-sm text-muted-foreground">No named warm-pool reservations.</p>
          )}
        </div>
      )}

      <CreatePoolEntryDialog open={createOpen} onOpenChange={setCreateOpen} />
      <PoolEntrySheet
        entry={selected}
        onClose={() => setSelectedId(null)}
        onManageContainer={(containerId) => {
          setSelectedId(null);
          // Parked pool brains get the admin bundle panel — hence ?bundles.
          navigate({
            to: "/superadmin/infrastructure/$containerId",
            params: { containerId },
            search: { bundles: true },
          });
        }}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

// Live domain list for the entry's container (Railway default + custom
// domains + boo-domain allocations) so the admin can eyeball what's actually
// mapped before renaming/deleting.
function DomainList({ entryId }: { entryId: string }) {
  const q = useQuery({
    queryKey: ["admin-named-warm-pool-domains", entryId],
    queryFn: () => api.admin.namedWarmPoolDomains(entryId),
  });
  return (
    <div className="space-y-1 rounded-md border bg-muted/30 p-3">
      <div className="text-xs font-medium">Domains currently on the linked brain</div>
      {q.isLoading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : q.data && q.data.domains.length > 0 ? (
        <ul className="space-y-0.5">
          {q.data.domains.map((d) => (
            <li key={d} className="font-mono text-[11px] break-all">
              {d}
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-xs text-muted-foreground">No live domains found.</div>
      )}
    </div>
  );
}

// One entry's detail + all actions live here so the table row stays lean.
function PoolEntrySheet({
  entry,
  onClose,
  onManageContainer,
}: {
  entry: AdminNamedWarmPoolRow | null;
  onClose: () => void;
  onManageContainer: (containerId: string) => void;
}) {
  const queryClient = useQueryClient();
  // Prefill the brand-build website with the first reserved domain (usually
  // the company's site); admin can edit before firing.
  const [sub, setSub] = useState(entry?.reservedSubdomain ?? "");
  const [website, setWebsite] = useState(entry?.domains[0] ?? "");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [prevEntryId, setPrevEntryId] = useState(entry?.id);
  if (prevEntryId !== entry?.id) {
    setPrevEntryId(entry?.id);
    if (entry) {
      setSub(entry.reservedSubdomain);
      setWebsite(entry.domains[0] ?? "");
    } else {
      // Sheet closed — clear abandoned edits so reopening the same entry
      // starts fresh.
      setSub("");
      setWebsite("");
      setConfirmDelete(false);
    }
  }

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin-named-warm-pool"] });

  const provision = useMutation({
    mutationFn: () => api.admin.provisionNamedWarmPoolEntry(entry!.id),
    onSuccess: () => {
      toast.success("Re-provisioning started");
      invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to start provisioning"),
  });

  const brandBuild = useMutation({
    mutationFn: () => api.admin.brandBuildNamedWarmPoolEntry(entry!.id, website.trim()),
    onSuccess: () => {
      toast.success("Brand build started — Boo is rewriting its brand skill");
      invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to start brand build"),
  });

  const change = useMutation({
    mutationFn: () => api.admin.changeNamedWarmPoolSubdomain(entry!.id, sub.trim().toLowerCase()),
    onSuccess: () => {
      toast.success("Re-issuing cert at the new subdomain…");
      invalidate();
      onClose();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to change subdomain"),
  });

  const del = useMutation({
    mutationFn: () => api.admin.deleteNamedWarmPoolEntry(entry!.id),
    onSuccess: (res) => {
      if (res.warning) toast.warning(res.warning);
      else toast.success(res.tornDown ? "Entry deleted and brain torn down" : "Entry removed");
      invalidate();
      setConfirmDelete(false);
      onClose();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed to delete"),
  });

  const claimed = !!entry?.claimedByOrgId;
  const status = entry ? statusOf(entry) : null;
  const norm = sub.trim().toLowerCase();
  const subValid = SUBDOMAIN_RE.test(norm) && norm !== entry?.reservedSubdomain;
  // Brand build + bundle install need a live brain (backend also enforces this).
  const brainRunning = !!entry?.containerId && entry?.containerStatus === "running";

  return (
    <Sheet open={!!entry} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
        {entry && (
          <>
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                {entry.companyName}
                {status && <Badge variant={STATUS_VARIANT[status]}>{status}</Badge>}
              </SheetTitle>
              <SheetDescription className="font-mono">{entry.reservedSubdomain}</SheetDescription>
            </SheetHeader>

            <div className="space-y-5 px-4 pb-10">
              <Field label="Branded URL">
                <div className="flex flex-col gap-0.5">
                  {entry.customDomainFqdn ? (
                    <a
                      href={`https://${entry.customDomainFqdn}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-mono text-xs text-primary hover:underline"
                    >
                      {entry.customDomainFqdn}
                      <ExternalLink className="size-3" />
                    </a>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                  {entry.railwayServiceUrl && (
                    <a
                      href={entry.railwayServiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-[10px] text-muted-foreground hover:underline break-all"
                    >
                      {entry.railwayServiceUrl.replace(/^https?:\/\//, "")}
                    </a>
                  )}
                </div>
              </Field>

              <Field label="Email domains">
                <div className="flex flex-wrap gap-1">
                  {entry.domains.map((d) => (
                    <Badge key={d} variant="secondary" className="font-mono text-[10px]">
                      {d}
                    </Badge>
                  ))}
                </div>
              </Field>

              <Field label="Pinned email">
                {entry.expectedFirstUserEmail ? (
                  <span className="font-mono text-xs">{entry.expectedFirstUserEmail}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </Field>

              {entry.notes && <Field label="Notes">{entry.notes}</Field>}

              <Field label="Created">{formatDate(entry.createdAt)}</Field>

              {claimed && (
                <Field label="Claimed by">
                  {entry.claimedByOrgName ?? entry.claimedByOrgId}
                  {entry.claimedAt && (
                    <span className="text-muted-foreground"> · {formatDate(entry.claimedAt)}</span>
                  )}
                </Field>
              )}

              <DomainList entryId={entry.id} />

              {claimed ? (
                <p className="border-t pt-5 text-sm text-muted-foreground">
                  This entry is claimed — it's live for a customer, so it can't be renamed or deleted
                  here.
                </p>
              ) : (
                <div className="space-y-6 border-t pt-5">
                  {status !== "Ready" && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Provisioning</div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={provision.isPending}
                        onClick={() => provision.mutate()}
                      >
                        {provision.isPending ? (
                          <Loader2 className="size-4 animate-spin" />
                        ) : (
                          <RotateCw className="size-3.5" />
                        )}
                        Retry provisioning
                      </Button>
                    </div>
                  )}

                  <div className="space-y-3">
                    <div className="text-sm font-medium">Pre-handoff setup</div>

                    <div className="space-y-1.5">
                      <Label htmlFor="sheet-website">Brand from website</Label>
                      <div className="flex gap-2">
                        <Input
                          id="sheet-website"
                          value={website}
                          onChange={(e) => setWebsite(e.target.value)}
                          placeholder="acme.com"
                        />
                        <Button
                          variant="outline"
                          disabled={!website.trim() || !brainRunning || brandBuild.isPending}
                          onClick={() => brandBuild.mutate()}
                        >
                          {brandBuild.isPending ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <Palette className="size-3.5" />
                          )}
                          Build brand
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Fetches the company's brand and has Boo rewrite its brand skill. Re-running
                        is safe — Boo skips it if it's already branded for this site.
                        {!brainRunning && " Available once the brain is running."}
                      </p>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!entry.containerId}
                      onClick={() => entry.containerId && onManageContainer(entry.containerId)}
                    >
                      <Boxes className="size-3.5" />
                      Manage container &amp; bundles
                    </Button>
                  </div>

                  <div className="space-y-2 border-t pt-5">
                    <Label htmlFor="sheet-sub">Reserved subdomain</Label>
                    <div className="flex gap-2">
                      <Input
                        id="sheet-sub"
                        value={sub}
                        onChange={(e) => setSub(e.target.value)}
                        aria-invalid={sub.length > 0 && !SUBDOMAIN_RE.test(norm)}
                      />
                      <Button
                        variant="outline"
                        disabled={!subValid || change.isPending}
                        onClick={() => change.mutate()}
                      >
                        {change.isPending && <Loader2 className="size-4 animate-spin" />}
                        Change
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Re-attaches the brain to{" "}
                      <span className="font-mono">brain.{norm || "…"}.boolab.io</span> (~1–2 min
                      re-cert; the row shows “Issuing cert” then “Ready”).
                    </p>
                  </div>

                  <div className="space-y-2 border-t pt-5">
                    <div className="text-sm font-medium text-destructive">Danger zone</div>
                    <p className="text-xs text-muted-foreground">
                      Deletes the reservation and, if the linked brain genuinely matches it, tears it
                      down completely — Railway project, domains, and credentials. A mismatched
                      container is left untouched (only the DB row is removed).
                    </p>
                    <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)}>
                      <Trash2 className="size-3.5" />
                      Delete entry
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <ConfirmDialog
              open={confirmDelete}
              onOpenChange={setConfirmDelete}
              title="Delete pool entry?"
              description={`Removes the reservation for ${entry.companyName} (${entry.reservedSubdomain}) and tears down its warm brain if it matches this reservation. This can't be undone.`}
              confirmLabel="Delete"
              loading={del.isPending}
              onConfirm={() => del.mutate()}
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function CreatePoolEntryDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [companyName, setCompanyName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [domainsCsv, setDomainsCsv] = useState("");
  const [pinnedEmail, setPinnedEmail] = useState("");
  const [notes, setNotes] = useState("");

  function reset() {
    setCompanyName("");
    setSubdomain("");
    setDomainsCsv("");
    setPinnedEmail("");
    setNotes("");
  }

  const domains = domainsCsv
    .split(",")
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);
  const subValid = SUBDOMAIN_RE.test(subdomain.trim().toLowerCase());
  const domainsValid = domains.length > 0 && domains.every((d) => d.includes("."));
  const canSubmit = companyName.trim().length > 0 && subValid && domainsValid;

  const create = useMutation({
    mutationFn: () =>
      api.admin.createNamedWarmPoolEntry({
        companyName: companyName.trim(),
        reservedSubdomain: subdomain.trim().toLowerCase(),
        domains,
        expectedFirstUserEmail: pinnedEmail.trim() || undefined,
        notes: notes.trim() || undefined,
      }),
    onSuccess: (res) => {
      toast.success(`Provisioning started for ${res.companyName} — ~3–5 min`);
      queryClient.invalidateQueries({ queryKey: ["admin-named-warm-pool"] });
      reset();
      onOpenChange(false);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Failed to create pool entry"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New pool entry</DialogTitle>
          <DialogDescription>
            Reserves a branded <span className="font-mono">&lt;subdomain&gt;.brain.boolab.io</span>{" "}
            and warms a Boo for this company. The first signup from a matching domain claims it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="pool-company">Company name</Label>
            <Input
              id="pool-company"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Cloud Physician"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pool-subdomain">Reserved subdomain</Label>
            <Input
              id="pool-subdomain"
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value)}
              placeholder="cloudphysician"
              aria-invalid={subdomain.length > 0 && !subValid}
            />
            <p className="text-xs text-muted-foreground">
              3–30 chars, lowercase letters, numbers, dashes. Becomes{" "}
              <span className="font-mono">brain.{subdomain.trim().toLowerCase() || "…"}.boolab.io</span>.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pool-domains">Email domains</Label>
            <Input
              id="pool-domains"
              value={domainsCsv}
              onChange={(e) => setDomainsCsv(e.target.value)}
              placeholder="cloudphysician.ai, cloudphysician.net"
              aria-invalid={domainsCsv.length > 0 && !domainsValid}
            />
            <p className="text-xs text-muted-foreground">
              Comma-separated. A signup whose email domain matches any of these claims this entry.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pool-email">Pinned first-user email (optional)</Label>
            <Input
              id="pool-email"
              value={pinnedEmail}
              onChange={(e) => setPinnedEmail(e.target.value)}
              placeholder="admin@cloudphysician.ai"
            />
            <p className="text-xs text-muted-foreground">
              Exact-match gate for shared/holding-company domains — only this email can claim it.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pool-notes">Notes (optional)</Label>
            <Input
              id="pool-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Sales lead — Q3 2026"
            />
          </div>

          <p className="text-xs text-muted-foreground">
            Heads up: this provisions real infrastructure (~3–5 min).
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={!canSubmit || create.isPending}>
            {create.isPending && <Loader2 className="size-4 animate-spin" />}
            Create &amp; provision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
